import { CircularProgress, Stack, Typography } from '@mui/material';
import { DEFAULT_TIMEZONE } from '../../../../constants/appointments';
import useGetCustomerTimeline from '../../../../hooks/customers/useGetCustomerTimeline';
import useUser from '../../../../hooks/global/useUser';
import { LoadedUser } from '../../../../interfaces';
import TimelineFeed from './TimelineFeed';
import LoadEarlierActivityLink from './LoadEarlierActivityLink';
import {
  RAIL_COLUMN_GAP,
  RAIL_NODE_WIDTH,
} from '../../../../constants/customers';

interface Props {
  customerId: string;
  businessId: string;
}

function TimelineTab({ customerId, businessId }: Props) {
  const { user } = useUser<LoadedUser>();
  const {
    entries,
    isInitialLoading,
    isFetchingNextPage,
    hasMore,
    loadMore,
    error,
  } = useGetCustomerTimeline({ customerId, businessId });

  if (isInitialLoading) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ height: '200px' }}>
        <CircularProgress color="primary" />
      </Stack>
    );
  }

  if (error && entries.length === 0) {
    return (
      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
        There was an error loading this customer&apos;s activity.
      </Typography>
    );
  }

  if (entries.length === 0) {
    return (
      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
        No activity yet.
      </Typography>
    );
  }

  return (
    <>
      <TimelineFeed
        entries={entries}
        hasMore={hasMore}
        timezone={user?.timezone ?? DEFAULT_TIMEZONE}
      />
      {error && (
        <Typography
          variant="body2"
          sx={{
            paddingLeft: `${RAIL_NODE_WIDTH + RAIL_COLUMN_GAP}px`,
            marginTop: '8px',
            color: 'error.dark',
          }}>
          Couldn&apos;t load earlier activity.
        </Typography>
      )}
      {hasMore && (
        <LoadEarlierActivityLink
          onClick={loadMore}
          isLoading={isFetchingNextPage}
        />
      )}
    </>
  );
}

export default TimelineTab;
